import { ArrowDownTrayIcon, FunnelIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import Badge from '../../../common/Badge';
import { showToast } from '../../../common/Notification';
import Popup from '../../../common/Popup/Popup.module';
import Spinner from '../../../common/Spinner';
import type { SciExportContent, SciExportHistoryItem } from './sciExport';
import { describeSciExportFilters, downloadSciExportWorkbook, sciExportFileName } from './sciExport';

const PAGE_SIZE = 10;

interface SCIExportHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  // Bumped after each new export, so that an open history shows it
  refreshKey: number;
  onApplyFilters: (filters: Record<string, string>) => void;
}

const authorOf = (item: SciExportHistoryItem): string => {
  if (!item.createdBy) {return '—';}
  const fullName = [item.createdBy.firstname, item.createdBy.lastname].filter(Boolean).join(' ');

  return fullName || item.createdBy.email;
};

const formatDate = (iso: string | null): string => (
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'
);

const SCIExportHistory: React.FC<SCIExportHistoryProps> = ({ isOpen, onClose, refreshKey, onApplyFilters }) => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const [items, setItems] = useState<SciExportHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Back to the first page once closed, so that reopening fetches it only once
  useEffect(() => {
    if (!isOpen) {setPage(1);}
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !company?.id || !jwt) {return undefined;}
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/companies/${company.id}/sci-exports?step=${page}&limit=${PAGE_SIZE}`, JWTHeader(jwt))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        if (cancelled) {return;}
        setItems(Array.isArray(data.results) ? data.results : []);
        setTotalPages(data.pagination?.totalPages ?? 1);
        setTotalResults(data.pagination?.totalResults ?? 0);
      })
      .catch(() => { if (!cancelled) {setError("Impossible de charger l'historique des exports.");} })
      .finally(() => { if (!cancelled) {setLoading(false);} });

    return () => { cancelled = true; };
  }, [isOpen, company?.id, jwt, page, refreshKey, reloadKey]);

  const handleDownload = async(item: SciExportHistoryItem) => {
    if (!company?.id || !jwt || downloadingId !== null) {return;}
    setDownloadingId(item.id);
    try {
      const resp = await fetch(`/api/companies/${company.id}/sci-exports/${item.id}/content`, JWTHeader(jwt));
      if (!resp.ok) {throw new Error(String(resp.status));}
      const data: SciExportContent = await resp.json();
      await downloadSciExportWorkbook(
        { headers: data.headers, rows: data.rows },
        sciExportFileName(item.name, item.createdAt ? new Date(item.createdAt) : new Date()),
      );
    } catch (e) {
      console.error(e);
      showToast('error', "Le fichier de cet export n'a pas pu être récupéré.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async(item: SciExportHistoryItem) => {
    if (!company?.id || !jwt || deletingId !== null) {return;}
    const label = item.campaign ? `l'export de la campagne « ${item.name} »` : `l'export du ${formatDate(item.createdAt)}`;
    if (!window.confirm(`Supprimer ${label} et son fichier ? Cette action est définitive.`)) {return;}
    setDeletingId(item.id);
    try {
      const resp = await fetch(`/api/companies/${company.id}/sci-exports/${item.id}`, { method: 'DELETE', headers: { Authorization: JWTAuthorization(jwt) } });
      if (!resp.ok) {throw new Error(String(resp.status));}
      showToast('success', 'Export supprimé.');
      // The last item of a page: show the previous one
      if (items.length === 1 && page > 1) { setPage(page - 1); } else { setReloadKey((key) => key + 1); }
    } catch (e) {
      console.error(e);
      showToast('error', "L'export n'a pas pu être supprimé.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Popup isOpen={isOpen} onClose={onClose} title={<span>Historique des exports SCI</span>} additionalClass="w-full max-w-4xl">
      <div className="text-sm">
        {error && <div className="my-4 text-center"><Badge mode="error" text={error} /></div>}
        {!error && loading && items.length === 0 && (
          <p className="my-6 text-center"><Spinner aria-label="Chargement" size="lg" /></p>
        )}
        {!error && !loading && items.length === 0 && (
          <p className="my-6 text-center text-gray-600">Aucun export pour le moment.</p>
        )}
        {items.length > 0 && (
          <div className={`overflow-x-auto ${loading ? 'opacity-60' : ''}`}>
            <table className="min-w-full border bg-white text-left">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Par</th>
                  <th className="px-3 py-2">Campagne</th>
                  <th className="px-3 py-2">Filtres</th>
                  <th className="px-3 py-2 text-right">SCI</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const labels = describeSciExportFilters(item.filters);

                  return (
                    <tr key={item.id} className="border-t align-top">
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(item.createdAt)}</td>
                      <td className="px-3 py-2">{authorOf(item)}</td>
                      <td className="px-3 py-2">
                        {item.campaign ? <span className="font-medium">{item.name}</span> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        {labels.length > 0 ? labels.map((label) => <div key={label}>{label}</div>) : <span className="text-gray-400">Aucun filtre</span>}
                      </td>
                      <td className="px-3 py-2 text-right">{item.rowCount.toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => handleDownload(item)}
                            disabled={downloadingId !== null}
                            className="flex items-center whitespace-nowrap text-rgpd-blue hover:underline disabled:opacity-50"
                          >
                            {downloadingId === item.id ? <Spinner size="sm" className="mr-1" /> : <ArrowDownTrayIcon className="mr-1 h-4 w-4" />}
                            Télécharger
                          </button>
                          <button
                            type="button"
                            onClick={() => onApplyFilters(item.filters)}
                            className="flex items-center whitespace-nowrap text-rgpd-blue hover:underline"
                          >
                            <FunnelIcon className="mr-1 h-4 w-4" /> Reprendre ces filtres
                          </button>
                          {item.canDelete && (
                            <button
                              type="button"
                              onClick={() => handleDelete(item)}
                              disabled={deletingId !== null}
                              className="flex items-center whitespace-nowrap text-red-600 hover:underline disabled:opacity-50"
                            >
                              {deletingId === item.id ? <Spinner size="sm" className="mr-1" /> : <TrashIcon className="mr-1 h-4 w-4" />}
                              Supprimer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
            <span>{totalResults} export{totalResults > 1 ? 's' : ''}</span>
            <div className="flex items-center gap-2">
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-50" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Précédent</button>
              <span>Page {page} / {totalPages}</span>
              <button type="button" className="rounded border px-2 py-1 disabled:opacity-50" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Suivant</button>
            </div>
          </div>
        )}
      </div>
    </Popup>
  );
};

export default SCIExportHistory;
